import { Component, Inject, OnDestroy, OnInit, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Title } from '@angular/platform-browser';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import * as Waves from 'node-waves';

import * as HttpService from 'app/http/service/index';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from '@core/services/config.service';
import { CoreLoadingScreenService } from '@core/services/loading-screen.service';
import { CoreTranslationService } from '@core/services/translation.service';

import { menu } from 'app/menu/menu';
import { locale as menuEnglish } from 'app/menu/i18n/en';
import { locale as menuFrench } from 'app/menu/i18n/fr';
import { locale as menuGerman } from 'app/menu/i18n/de';
import { locale as menuPortuguese } from 'app/menu/i18n/pt';
import { locale as menuSpanish } from 'app/menu/i18n/es';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

interface RequestError { problem: string; solution: string; by: string; }
interface CurrentError {
  problem: string;
  solution: string;
  by: string;
  id: number;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('modalError') modalError: any;
  @ViewChild('modalTicket') modalTicket: any;
  errors: { all: RequestError[]; current: CurrentError } = {
    all: [],
    current: {
      problem: 'No hay ningún error.',
      solution: 'No hay ningún error.',
      by: 'Usuario',
      id: 0,
    },
  };
  ticket = '';
  coreConfig: any;
  menu: any;
  defaultLanguage: 'en'; // This language will be used as a fallback when a translation isn't found in the current language
  appLanguage: 'en'; // Set application default language i.e fr

  // Private
  private _unsubscribeAll: Subject<any>;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private _title: Title,
    private _renderer: Renderer2,
    private _elementRef: ElementRef,
    private _modalService: NgbModal,
    private _coreMenuService: CoreMenuService,
    private _translateService: TranslateService,
    public _coreConfigService: CoreConfigService,
    private _coreSidebarService: CoreSidebarService,
    private _httpResponseService: HttpService.ResponseService,
    private _coreLoadingScreenService: CoreLoadingScreenService,
    private _coreTranslationService: CoreTranslationService,
  ) {
    // Get the application main menu
    this.menu = menu;

    // Register the menu to the menu service
    this._coreMenuService.register('main', this.menu);

    // Set the main menu as our current menu
    this._coreMenuService.setCurrentMenu('main');

    // Add languages to the translation service
    this._translateService.addLangs(['en', 'fr', 'de', 'pt', 'es']);

    // This language will be used as a fallback when a translation isn't found in the current language
    this._translateService.setDefaultLang('es');

    // Set the translations for the menu
    this._coreTranslationService.translate(menuEnglish, menuFrench, menuGerman, menuPortuguese, menuSpanish);

    // Set the private defaults
    this._unsubscribeAll = new Subject();

    // Suscríbase al sistema de tickets y comentarios de https
    this.modalOpenError();
    this.modalOpenTicket();
  }

  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Init wave effect (Ripple effect)
    Waves.init();

    // Subscribe to config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.coreConfig = config;

      // Set application default language.

      // Change application language? Read the ngxTranslate Fix

      // ? Use app-config.ts file to set default language
      const appLanguage = this.coreConfig.app.appLanguage || 'es';
      this._translateService.use(appLanguage);

      // ? OR
      // ? User the current browser lang if available, if undefined use 'en'
      // const browserLang = this._translateService.getBrowserLang();
      // this._translateService.use(browserLang.match(/en|fr|de|pt/) ? browserLang : 'en');

      /**
       * ! Fix : ngxTranslate
       * ----------------------------------------------------------------------------------------------------
       */

      /**
       *
       * Using different language than the default ('en') one i.e French?
       * In this case, you may find the issue where application is not properly translated when your app is initialized.
       *
       * It's due to ngxTranslate module and below is a fix for that.
       * Eventually we will move to the multi language implementation over to the Angular's core language service.
       *
       **/

      // Set the default language to 'en' and then back to 'fr'.

      // setTimeout(() => {
      //   this._translateService.setDefaultLang('en');
      //   this._translateService.setDefaultLang(appLanguage);
      // });

      /**
       * !Fix: ngxTranslate
       * ----------------------------------------------------------------------------------------------------
       */

      // Layout
      // --------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove(
        'vertical-layout',
        'vertical-menu-modern',
        'horizontal-layout',
        'horizontal-menu'
      );
      // Add class based on config options
      if (this.coreConfig.layout.type === 'vertical') {
        this._elementRef.nativeElement.classList.add('vertical-layout', 'vertical-menu-modern');
      } else if (this.coreConfig.layout.type === 'horizontal') {
        this._elementRef.nativeElement.classList.add('horizontal-layout', 'horizontal-menu');
      }

      // Navbar
      // --------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove('navbar-floating', 'navbar-static', 'navbar-sticky', 'navbar-hidden');

      // Add class based on config options
      if (this.coreConfig.layout.navbar.type === 'navbar-static-top') {
        this._elementRef.nativeElement.classList.add('navbar-static');
      } else if (this.coreConfig.layout.navbar.type === 'fixed-top') {
        this._elementRef.nativeElement.classList.add('navbar-sticky');
      } else if (this.coreConfig.layout.navbar.type === 'floating-nav') {
        this._elementRef.nativeElement.classList.add('navbar-floating');
      } else {
        this._elementRef.nativeElement.classList.add('navbar-hidden');
      }

      // Footer
      // --------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove('footer-fixed', 'footer-static', 'footer-hidden');

      // Add class based on config options
      if (this.coreConfig.layout.footer.type === 'footer-sticky') {
        this._elementRef.nativeElement.classList.add('footer-fixed');
      } else if (this.coreConfig.layout.footer.type === 'footer-static') {
        this._elementRef.nativeElement.classList.add('footer-static');
      } else {
        this._elementRef.nativeElement.classList.add('footer-hidden');
      }

      // Blank layout
      if (this.coreConfig.layout.menu.hidden && this.coreConfig.layout.navbar.hidden && this.coreConfig.layout.footer.hidden) {
        this._elementRef.nativeElement.classList.add('blank-page');
        // ! Fix: Transition issue while coming from blank page
        this._renderer.setAttribute(
          this._elementRef.nativeElement.getElementsByClassName('app-content')[0],
          'style',
          'transition:none'
        );
      } else {
        this._elementRef.nativeElement.classList.remove('blank-page');
        // ! Fix: Transition issue while coming from blank page
        setTimeout(() => {
          this._renderer.setAttribute(
            this._elementRef.nativeElement.getElementsByClassName('app-content')[0],
            'style',
            'transition:300ms ease all'
          );
        }, 0);
        // If navbar hidden
        if (this.coreConfig.layout.navbar.hidden) {
          this._elementRef.nativeElement.classList.add('navbar-hidden');
        }
        // Menu (Vertical menu hidden)
        if (this.coreConfig.layout.menu.hidden) {
          this._renderer.setAttribute(this._elementRef.nativeElement, 'data-col', '1-column');
        } else {
          this._renderer.removeAttribute(this._elementRef.nativeElement, 'data-col');
        }
        // Footer
        if (this.coreConfig.layout.footer.hidden) {
          this._elementRef.nativeElement.classList.add('footer-hidden');
        }
      }

      // Skin Class (Adding to body as it requires highest priority)
      if (this.coreConfig.layout.skin !== '' && this.coreConfig.layout.skin !== undefined) {
        this.document.body.classList.remove('default-layout', 'bordered-layout', 'dark-layout', 'semi-dark-layout');
        this.document.body.classList.add(this.coreConfig.layout.skin + '-layout');
      }
    });

    // Set the application page title
    this._title.setTitle(this.coreConfig.app.appTitle);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  modalOpenError() {
    this._httpResponseService.$errors.subscribe((data) => {
      this.errors.all = data;
      this.errors.current.by = data[0].by;
      this.errors.current.problem = data[0].problem;
      this.errors.current.solution = data[0].solution;
      this.errors.current.id = 0;
      this._modalService.open(this.modalError, {
        centered: true,
        windowClass: 'modal modal-danger',
      });
    });
  }


  modalOpenTicket() {
    this._httpResponseService.$ticket.subscribe((ticket) => {
      this.ticket = ticket;
      this._modalService.open(this.modalTicket, {
        centered: true,
        windowClass: 'modal modal-info',
      });
    });
  }

  changeError(action: 'back' | 'next') {
    if (action === 'back') {
      if (this.errors.current.id === 0) {
        this.errors.current.by = this.errors.all[this.errors.all.length - 1].by;
        this.errors.current.problem = this.errors.all[this.errors.all.length - 1].problem;
        this.errors.current.solution = this.errors.all[this.errors.all.length - 1].solution;
        this.errors.current.id = this.errors.all.length - 1;
      } else {
        this.errors.current.by = this.errors.all[this.errors.current.id - 1].by;
        this.errors.current.problem = this.errors.all[this.errors.current.id - 1].problem;
        this.errors.current.solution = this.errors.all[this.errors.current.id - 1].solution;
        this.errors.current.id = this.errors.current.id - 1;
      }
    } else {
      if (this.errors.current.id === this.errors.all.length - 1) {
        this.errors.current.by = this.errors.all[0].by;
        this.errors.current.problem = this.errors.all[0].problem;
        this.errors.current.solution = this.errors.all[0].solution;
        this.errors.current.id = 0;
      } else {
        this.errors.current.by = this.errors.all[this.errors.current.id + 1].by;
        this.errors.current.problem = this.errors.all[this.errors.current.id + 1].problem;
        this.errors.current.solution = this.errors.all[this.errors.current.id + 1].solution;
        this.errors.current.id = this.errors.current.id + 1;
      }
    }
  }
}
